import { useEffect, useReducer } from "react";
import { useFormContext } from "react-hook-form";
import {
  isExistingMember,
  isNewMember,
  isSignupStep,
  isUserNotFound,
  isUserWithPendingInvitation,
} from "../helpers";
import {
  EMAIL_VERIFICATION_ACTION_TYPES,
  EMAIL_VERIFICATION_STEPS,
  type EmailVerificationAction,
  type EmailVerificationState,
  type EmailVerificationStatus,
  type EmailVerificationStep,
} from "../types";
import { useEmailVerificationHandlers } from "./useEmailVerificationHandlers";
import { useEmailVerificationMutations } from "./useEmailVerificationMutations";

const determineNextStep = (
  status: EmailVerificationStatus,
): EmailVerificationStep => {
  if (isUserNotFound(status)) {
    return EMAIL_VERIFICATION_STEPS.signup;
  }
  if (
    isExistingMember(status) ||
    isNewMember(status) ||
    isUserWithPendingInvitation(status)
  ) {
    return EMAIL_VERIFICATION_STEPS.otp_required;
  }
  return EMAIL_VERIFICATION_STEPS.initial;
};

const emailVerificationReducer = (
  state: EmailVerificationState,
  action: EmailVerificationAction,
): EmailVerificationState => {
  switch (action.type) {
    case EMAIL_VERIFICATION_ACTION_TYPES.SET_EMAIL:
      return { ...state, email: action.payload, error: null };
    case EMAIL_VERIFICATION_ACTION_TYPES.START_VALIDATION:
      return { ...state, isLoading: true, error: null };
    case EMAIL_VERIFICATION_ACTION_TYPES.VALIDATION_SUCCESS:
      return {
        ...state,
        isLoading: false,
        status: action.payload.status,
        step: determineNextStep(action.payload.status),
        error: null,
      };
    case EMAIL_VERIFICATION_ACTION_TYPES.VALIDATION_ERROR:
      return {
        ...state,
        isLoading: false,
        error: action.payload || null,
      };
    case EMAIL_VERIFICATION_ACTION_TYPES.START_OTP_SUBMISSION:
      return { ...state, isSubmittingOTP: true, error: null };
    case EMAIL_VERIFICATION_ACTION_TYPES.OTP_SUBMISSION_SUCCESS:
      return {
        ...state,
        isSubmittingOTP: false,
        verifiedOTP: action.payload?.otp || null,
      };
    case EMAIL_VERIFICATION_ACTION_TYPES.OTP_SUBMISSION_ERROR:
      return { ...state, isSubmittingOTP: false, error: action.payload };
    case EMAIL_VERIFICATION_ACTION_TYPES.START_RESEND_OTP:
      return { ...state, isResendingOTP: true, error: null };
    case EMAIL_VERIFICATION_ACTION_TYPES.RESEND_OTP_SUCCESS:
      return { ...state, isResendingOTP: false };
    case EMAIL_VERIFICATION_ACTION_TYPES.RESEND_OTP_ERROR:
      return { ...state, isResendingOTP: false, error: action.payload };
    case EMAIL_VERIFICATION_ACTION_TYPES.START_PASSWORD_SUBMISSION:
      return { ...state, isSubmittingPassword: true, error: null };
    case EMAIL_VERIFICATION_ACTION_TYPES.PASSWORD_SUBMISSION_SUCCESS:
      return { ...state, isSubmittingPassword: false };
    case EMAIL_VERIFICATION_ACTION_TYPES.PASSWORD_SUBMISSION_ERROR:
      return { ...state, isSubmittingPassword: false, error: action.payload };
    case EMAIL_VERIFICATION_ACTION_TYPES.START_FORGOT_PASSWORD_SUBMISSION:
      return { ...state, isSubmittingForgotPassword: true, error: null };
    case EMAIL_VERIFICATION_ACTION_TYPES.FORGOT_PASSWORD_SUBMISSION_SUCCESS:
      return { ...state, isSubmittingForgotPassword: false };
    case EMAIL_VERIFICATION_ACTION_TYPES.FORGOT_PASSWORD_SUBMISSION_ERROR:
      return {
        ...state,
        isSubmittingForgotPassword: false,
        error: action.payload,
      };
    case EMAIL_VERIFICATION_ACTION_TYPES.SHOW_OTP_FORM:
      return {
        ...state,
        step: EMAIL_VERIFICATION_STEPS.otp_required,
        error: null,
      };
    case EMAIL_VERIFICATION_ACTION_TYPES.SHOW_PASSWORD_FORM:
      return {
        ...state,
        step: EMAIL_VERIFICATION_STEPS.password_signin,
        error: null,
      };
    case EMAIL_VERIFICATION_ACTION_TYPES.SHOW_SIGNUP_FORM:
      return { ...state, step: EMAIL_VERIFICATION_STEPS.signup, error: null };
    case EMAIL_VERIFICATION_ACTION_TYPES.SHOW_CLAIM_FORM:
      return { ...state, step: EMAIL_VERIFICATION_STEPS.claim, error: null };
    case EMAIL_VERIFICATION_ACTION_TYPES.RESET_TO_INITIAL:
      return {
        ...state,
        step: EMAIL_VERIFICATION_STEPS.initial,
        status: null,
        verifiedOTP: null,
        error: null,
        shouldShowAuthenticationLink: true,
      };
    default:
      return state;
  }
};

const initialState: EmailVerificationState = {
  step: EMAIL_VERIFICATION_STEPS.initial,
  email: "",
  status: null,
  verifiedOTP: null,
  isLoading: false,
  isSubmittingOTP: false,
  isResendingOTP: false,
  isSubmittingPassword: false,
  isSubmittingForgotPassword: false,
  error: null,
  shouldShowAuthenticationLink: false,
};

export const useEmailVerification = () => {
  const [state, dispatch] = useReducer(emailVerificationReducer, initialState);
  const {
    setValue,
    formState: { errors },
    setFocus,
  } = useFormContext();

  const mutations = useEmailVerificationMutations({ dispatch });

  const handlers = useEmailVerificationHandlers({
    state,
    dispatch,
    setValue,
    mutations,
  });

  // Watch for email field errors and reset to initial state if there's an error
  useEffect(() => {
    if (errors.email && isSignupStep(state.step)) {
      setTimeout(() => {
        dispatch({ type: EMAIL_VERIFICATION_ACTION_TYPES.RESET_TO_INITIAL });
        setValue("password", "");
        setFocus("email");
      }, 0);
    }
  }, [errors.email, state.step, setValue, setFocus]);

  return {
    state,
    verifyEmail: mutations.verifyEmailMutation.mutateAsync,
    ...handlers,
  };
};
